// Dependencies
import React from "react";

// hooks
import { getReviewColumns } from "./columns";

// components
import Input from "../../../../../../../Components/Inputs/Text";
import Table from "../../../../../../../Components/TableLite";

// styles
import css from "../../DeletedObjects.styl";
import DeletedObjectWarningModal from "../../DeletedObjectWarningModal";
import useRequest from "../../../../../../../Hooks/useRequest";
import DeletedObjectConfirmationModal from "../../DeletedObjectConfirmationModal";

const DeletedReviews = (props) => {
	const {
		companyId,
		filterState,
		handleChange,
		isLoading,
		tableData,
		setTableData,
		deletedObjectWarningModalState,
		deletedObjectConfirmationModalState,
		setDeletedObjectWarningModalState,
		setDeletedObjectConfirmationModalState,
	} = props;

	const useUndeleteRequestResponse = useRequest(
		"UndeleteReview",
		{ filters: { companyId: companyId, reviewId: deletedObjectConfirmationModalState.deletedObjectId } },
		{ manual: true }
	);

	const confirmUndeleteReview = (reviewRow) => {
		setDeletedObjectConfirmationModalState({
			displayDeletedObjectConfirmationModal: true,
			deletedObjectId: reviewRow.id,
		});
	};

	/**
	 * Undeletes the review
	 */
	const undeleteReview = async () => {
		try {
			const response = await useUndeleteRequestResponse.revalidate();
			const success = response.data?.get("success");

			// Check for errors in the response and display warning modal if exists
			if (!success) {
				const responseError = response.data?.get("error");
				setDeletedObjectWarningModalState({
					displayDeletedObjectWarningModal: true,
					message: responseError || "An unexpected error occurred while undeleting the review.",
				});
			} else {
				// Remove the row from display in the table (only if undelete was successful)
				const newTableData = tableData.filter((row) => {
					return row.id !== deletedObjectConfirmationModalState.deletedObjectId;
				});
				setTableData(newTableData);

				// Hides the confirmation modal
				setDeletedObjectConfirmationModalState({
					...deletedObjectConfirmationModalState,
					displayDeletedObjectConfirmationModal: false,
				});
			}
		} catch (error) {
			// Handle any unexpected errors during the request
			console.error("Error undeleting review:", error);
			setDeletedObjectWarningModalState({
				displayDeletedObjectWarningModal: true,
				message: "An unexpected error occurred while undeleting the review.",
			});
		}
	};

	const columns = getReviewColumns({ confirmUndeleteReview });

	return (
		<>
			<div className={css.filters}>
				<div className={css.filtersContainer}>
					<div className={css.filterBox}>
						<label>ID</label>
						<div className={css.filterInput}>
							<Input value={filterState.id} onChange={(value) => handleChange("id", value)} />
						</div>
					</div>

					<div className={css.filterBox}>
						<label>Review Name</label>
						<div className={css.filterInput}>
							<Input value={filterState.name} onChange={(value) => handleChange("name", value)} />
						</div>
					</div>

					<div className={css.filterBox}>
						<label>Review Cycle Name</label>
						<div className={css.filterInput}>
							<Input
								value={filterState.reviewCycleName}
								onChange={(value) => handleChange("reviewCycleName", value)}
							/>
						</div>
					</div>

					<div className={css.filterBox}>
						<label>Subject</label>
						<div className={css.filterInput}>
							<Input value={filterState.subject} onChange={(value) => handleChange("subject", value)} />
						</div>
					</div>
				</div>
			</div>

			<div className={css.results}>
				<div className={css.tableContainer}>
					<div className={css.tableWrapper}>
						<Table isLoading={isLoading} bodyCellClass={css.cell} columns={columns} rows={tableData} />
					</div>
				</div>
			</div>

			{deletedObjectWarningModalState.displayDeletedObjectWarningModal ? (
				<DeletedObjectWarningModal
					handleClose={() => setDeletedObjectWarningModalState({ displayDeletedObjectWarningModal: false })}
					message={deletedObjectWarningModalState.message}
				/>
			) : null}
			{deletedObjectConfirmationModalState.displayDeletedObjectConfirmationModal ? (
				<DeletedObjectConfirmationModal
					handleCancel={() =>
						setDeletedObjectConfirmationModalState({ displayDeletedObjectConfirmationModal: false })
					}
					handleConfirm={undeleteReview}
					message="Are you sure you want to undelete this review?"
					deletedObjectId={deletedObjectConfirmationModalState.deletedObjectId}
				/>
			) : null}
		</>
	);
};

export default DeletedReviews;
